import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import {
  PaginationDropdown,
  CustomConfirmModal,
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { Minus, Plus, Download } from "lucide-react";
import Loader from "../partials/Loader";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchData } from "../../utils/functions";
import GeneralFooter from "../common/GeneralFooter";
import { setExporting } from "../../slices/exportSlice";
import { logout } from "../../slices/authSlice";

const ImplementationPhaseFeatuesList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { village_id } = useParams();

  // Get auth state from Redux
  const loading = useSelector((state) => state);
  const isExporting = useSelector((state) => state.export.isExporting);
  const userRole = loading?.auth?.user?.user_role_name || "";
  const permission = loading?.auth?.user?.user_permissions || [];

  // Check permissions
  const isSuperAdmin = userRole === "Super Admin";

  // Modal states
  const [isApproveModalVisible, setApproveModalVisible] = useState(false);
  const [isRejectModalVisible, setRejectModalVisible] = useState(false);
  const [isRemarksModalVisible, setRemarksModalVisible] = useState(false);
  const [isViewRemarksModalVisible, setViewRemarksModalVisible] =
    useState(false);
  const [remarks, setRemarks] = useState("");
  const [remarksError, setRemarksError] = useState("");
  const [selectedApproveId, setSelectedApproveId] = useState(null);
  const [selectedRejectId, setSelectedRejectId] = useState(null);
  const [selectedFeatureForRemarks, setSelectedFeatureForRemarks] =
    useState(null);

  // UI state
  const [features, setFeatures] = useState([]);
  const [villageInfo, setVillageInfo] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [search, setSearch] = useState("");
  const [rolesData, setRolesData] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  // Filter states
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [filterReset, setFilterReset] = useState(0);

  // DQA Approval filter
  const [selectedDqaApproval, setSelectedDqaApproval] = useState(null);
  const [appliedDqaApproval, setAppliedDqaApproval] = useState(null);

  // PM Approval filter
  const [selectedPmApproval, setSelectedPmApproval] = useState(null);
  const [appliedPmApproval, setAppliedPmApproval] = useState(null);

  // Date filters
  const [dateField, setDateField] = useState("");
  const [condition, setCondition] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedDateField, setAppliedDateField] = useState(null);
  const [appliedCondition, setAppliedCondition] = useState(null);
  const [appliedDateValue, setAppliedDateValue] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);

  const hasFiltersApplied =
    appliedDqaApproval || appliedPmApproval || appliedCondition;

  const hasDataToExport = rolesData.length > 0;

  const approvalOptions = [
    { value: "1", label: "Approved" },
    { value: "2", label: "Rejected" },
    { value: "0", label: "Pending" },
    { value: "null", label: "Not Verified" },
  ];

  const dateFieldsOptions = [
    { value: "plan_date", label: "Plan Date" },
    { value: "dqa_approved_at", label: "DQA Approved Date" },
    { value: "pm_approved_at", label: "PM Approved Date" },
  ];

  const conditions = [
    { value: "lt", label: "Less Than" },
    { value: "gt", label: "Greater Than" },
    { value: "equals", label: "Equal To" },
    { value: "between", label: "Between" },
  ];
  const validateRemarks = (remarksText) => {
    if (!remarksText.trim()) {
      return "Remarks are required";
    }
    if (remarksText.trim().length < 10) {
      return "Remarks must be at least 10 characters long";
    }
    if (remarksText.trim().length > 255) {
      return "Remarks cannot exceed 255 characters";
    }
    return "";
  };

  // Handle remarks change
  const handleRemarksChange = (value) => {
    setRemarks(value);
    if (remarksError) {
      const error = validateRemarks(value);
      setRemarksError(error);
    }
  };

  const getRemarksHistory = (feature) => {
    if (!feature.auditLogs || feature.auditLogs.length === 0) {
      return [];
    }
    const history = [];
    feature.auditLogs.forEach((log, index) => {
      try {
        const newValue = JSON.parse(log.newvalue);
        const oldValue = log.oldvalue ? JSON.parse(log.oldvalue) : null;

        history.push({
          action: log.action,
          date: log.createdAt,
          from: oldValue?.remarks || oldValue?.newRemarks || "None",
          to: newValue.remarks || newValue.newRemarks || "N/A",
          userId: log.userId,
          userName: log.userName,
        });

      } catch (error) {
        console.warn("Failed to parse audit log:", error);
      }
    });

    return history;
  };

  // Get latest remarks
  const getLatestRemarks = (feature) => {
    if (feature.auditLogs && feature.auditLogs.length > 0) {
      try {
        const latestAudit = feature.auditLogs[0];
        const newValue = JSON.parse(latestAudit.newvalue);
        return (
          newValue.remarks ||
          newValue.newRemarks ||
          feature.rejection_remarks ||
          "No remarks"
        );
      } catch (error) {
        console.warn("Failed to parse audit log:", error);
        return feature.rejection_remarks || "No remarks";
      }
    }
    return feature.rejection_remarks || "No remarks";
  };
  const hasAnyRemarks = (feature) => {
    if (feature.rejection_remarks && feature.rejection_remarks.trim() !== "") {
      return true;
    }
    // Check audit logs
    const history = getRemarksHistory(feature);
    return history.length > 0;
  };

  // Show remarks dialog
  const showRemarksDialog = (feature) => {
    setSelectedFeatureForRemarks(feature);
    setViewRemarksModalVisible(true);
  };
  const closeRemarksDialog = () => {
    setViewRemarksModalVisible(false);
    setSelectedFeatureForRemarks(null);
  };
  const formatDateTimeForDisplay = (dateTimeString) => {
    if (!dateTimeString) return "N/A";

    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) {
        console.error("Invalid date string:", dateTimeString);
        return "Invalid Date";
      }

      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Karachi",
      });
    } catch (error) {
      console.error("Error formatting date:", error, dateTimeString);
      return "Invalid Date";
    }
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const handleApply = () => {
    if (condition && !dateField) {
      toast.error("Please select a date field when using a condition");
      return;
    }

    if (condition === "between") {
      if (!startDate || !endDate) {
        toast.error(
          "Please provide both start and end dates for 'between' condition",
        );
        return;
      }
    }

    if (condition && condition !== "between" && !dateValue) {
      toast.error("Please provide a date value for the selected condition");
      return;
    }

    setAppliedDqaApproval(selectedDqaApproval);
    setAppliedPmApproval(selectedPmApproval);
    setAppliedDateField(dateField);
    setAppliedCondition(condition);

    if (condition === "between") {
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
      setAppliedDateValue(null);
    } else {
      setAppliedDateValue(dateValue);
      setAppliedStartDate(null);
      setAppliedEndDate(null);
    }

    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleClear = () => {
    setSelectedDqaApproval(null);
    setSelectedPmApproval(null);
    setAppliedDqaApproval(null);
    setAppliedPmApproval(null);
    setAppliedDateField(null);
    setAppliedCondition(null);
    setAppliedDateValue(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setDateField("");
    setCondition("");
    setDateValue("");
    setStartDate("");
    setEndDate("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setFilterReset((prev) => prev + 1);
  };

  const handleFilterChange = (key, value) => {
    switch (key) {
      case "dateField":
        setDateField(value);
        if (value === "") {
          setCondition("");
          setDateValue("");
          setStartDate("");
          setEndDate("");
        }
        break;
      case "condition":
        setCondition(value);
        if (value !== "between") {
          setStartDate("");
          setEndDate("");
        } else {
          setDateValue("");
        }
        break;
      default:
        break;
    }
  };

  const handleExport = async () => {
    dispatch(setExporting(true));

    try {
      toast.info("Preparing export... This may take a moment.");
      const params = new URLSearchParams();

      const boqsProposedIds = features.map((f) => f.id);
      if (boqsProposedIds.length) {
        params.append("boqs_proposed_ids", boqsProposedIds.join(","));
      }

      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found.");

      const response = await fetch(
        `${BASE_URL}/api/implementation-phase-details/export-by-id?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        },
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          dispatch(logout());
          navigate("/login");
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Export failed");
        }
        throw new Error(`Export failed with status: ${response.status}`);
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "implementation_phase_export.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, "");
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Export completed! Downloaded: ${filename}`);
    } catch (error) {
      console.error("Error during export:", error);
      toast.error(error.message || "Failed to export data. Please try again.");
    } finally {
      dispatch(setExporting(false));
    }
  };

  //date time formatter function
  const formatApprovalDateTime = (dateTimeString) => {

    if (!dateTimeString) return null;

    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error, dateTimeString);
      return "Error";
    }
  };
  const formatDateSubmitted = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const parseDDMMYYYY = useCallback((str) => {
    if (!str) return null;
    const parts = str.split("/");
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map(Number);
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  }, []);

  // Helper: parse yyyy-mm-dd to Date
  const parseYYYYMMDD = useCallback((str) => {
    if (!str) return null;
    const parts = str.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts.map(Number);
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  }, []);

  // Helper function to determine button visibility
  const getButtonVisibility = (feature) => {
    const isDQAApproved = feature.is_dqa_approved === 1;
    const isDQARejected = feature.is_dqa_approved === 2;
    const isDQAPending =
      feature.is_dqa_approved === 0 || feature.is_dqa_approved === null;

    const isPMApproved = feature.is_pm_approved === 1;
    const isPMRejected = feature.is_pm_approved === 2;
    const isPMPending =
      feature.is_pm_approved === 0 || feature.is_pm_approved === null;

    return {
      // Show DQA buttons when DQA is pending (0 or null)
      showDqaButtons: isDQAPending,

      // Show PM buttons when:
      // 1. DQA is approved AND PM is pending, OR
      // 2. DQA is rejected AND PM is pending (PM can override DQA rejection)
      showPmButtons: (isDQAApproved || isDQARejected) && isPMPending,

      // Show enumerator resolve form when DQA has rejected (2)
      showEnumeratorResolveForm: isDQARejected,

      // Hide all buttons when PM has made a decision (1 or 2) after DQA approval/rejection
      hideAllButtons:
        (isDQAApproved || isDQARejected) && (isPMApproved || isPMRejected),
    };
  };
  const fetchFeaturesFromAPI = useCallback(async () => {
    try {
      const response = await fetchData(
        `implementation-phase-details/${village_id}`,
        "GET",
        null,
        {},
        {},
        navigate,
        null,
        dispatch,
      );

      if (response?.success && response?.data?.implementation_information) {
        const featuresData = response.data.implementation_information;

        
        const features = featuresData.map((item) => {
          return {
            id: item.boqs_proposed_id,
            village_name: item.village_name,
            boqs_proposed_implementation_id:
              item.boqs_proposed_implementation_id,
            feature: item.feature_name || "N/A",
            category_name: item.category_name || "N/A",
            feature_code: item.category_code || "N/A",
            overall_status: item.overall_status || "N/A",
            record_date: item.record_date
              ? formatDateSubmitted(item.record_date)
              : "N/A",
            is_pm_approved: item.verified_from_pm,
            pm_approved_at:
              item.verified_from_pm === 1
                ? item.verified_from_pm_datetime
                : null,
            pm_rejected_at:
              item.verified_from_pm === 2
                ? item.verified_from_pm_datetime
                : null,
            is_dqa_approved: item.verified_from_dqa,
            dqa_approved_at:
              item.verified_from_dqa === 1
                ? item.verified_from_dqa_datetime
                : null,
            dqa_rejected_at:
              item.verified_from_dqa === 2
                ? item.verified_from_dqa_datetime
                : null,
            rejection_remarks: item.rejection_remarks || null,
            auditLogs: item.auditLogs || [],
            // Store raw timestamps from database
            verified_from_pm_datetime: item.verified_from_pm_datetime,
            verified_from_dqa_datetime: item.verified_from_dqa_datetime,
            created_at: item.created_at || null,
            updated_at: item.updated_at || null,
            plan_date: item.plan_date || null,
          };
        });

        return {
          features,
          villageInfo: { village_id },
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching implementation details:", error);
      toast.error("Failed to fetch implementation phase details");
      return null;
    }
  }, [village_id, navigate, dispatch]);

  const getFilteredFeatures = useCallback(
    (allFeatures) => {
      if (!allFeatures || allFeatures.length === 0) return [];

      const dqaFilter =
        appliedDqaApproval && appliedDqaApproval !== "Select an option"
          ? appliedDqaApproval
          : null;

      const pmFilter =
        appliedPmApproval && appliedPmApproval !== "Select an option"
          ? appliedPmApproval
          : null;

      const start = appliedStartDate ? parseYYYYMMDD(appliedStartDate) : null;
      const end = appliedEndDate ? parseYYYYMMDD(appliedEndDate) : null;
      const dateVal = appliedDateValue ? parseYYYYMMDD(appliedDateValue) : null;

      const normalizedUserRole = userRole.toLowerCase();

      return allFeatures.filter((f) => {
        // --- Role based filter ---
        // Project Managers should see ALL records, regardless of DQA approval status
        // The condition for hiding buttons is handled in the UI, not in data filtering
        if (normalizedUserRole === "project manager") {
          // PM can see all records - no filtering based on approval status
          // Just continue to other filters
        } else if (
          !isSuperAdmin &&
          normalizedUserRole !== "dqa" &&
          normalizedUserRole !== "project manager" &&
          normalizedUserRole !== "technical lead"
        ) {
          return false;
        }

        // --- DQA Approval ---
        if (dqaFilter !== null) {
          if (dqaFilter === "null") {
            // For pending/not verified: 0, null
            if (f.is_dqa_approved === 1 || f.is_dqa_approved === 2)
              return false;
          } else if (dqaFilter === "1") {
            // For approved: only 1
            if (f.is_dqa_approved !== 1) return false;
          } else if (dqaFilter === "2") {
            // For rejected: only 2
            if (f.is_dqa_approved !== 2) return false;
          } else if (dqaFilter === "0") {
            // For pending: only 0 (not null)
            if (f.is_dqa_approved !== 0) return false;
          }
        }

        // --- PM Approval ---
        if (pmFilter !== null) {
          if (pmFilter === "null") {
            // For pending/not verified: 0, null
            if (f.is_pm_approved === 1 || f.is_pm_approved === 2) return false;
          } else if (pmFilter === "1") {
            // For approved: only 1
            if (f.is_pm_approved !== 1) return false;
          } else if (pmFilter === "2") {
            // For rejected: only 2
            if (f.is_pm_approved !== 2) return false;
          } else if (pmFilter === "0") {
            // For pending: only 0 (not null)
            if (f.is_pm_approved !== 0) return false;
          }
        }

        // --- Date filter ---
        if (appliedDateField && appliedCondition) {
          const fieldVal = f[appliedDateField];
          if (!fieldVal) return false;

          const recordDate = parseDDMMYYYY(fieldVal);
          if (!recordDate) return false;

          switch (appliedCondition) {
            case "equals":
              if (!dateVal) return true;
              if (recordDate.getTime() !== dateVal.getTime()) return false;
              break;
            case "lt":
              if (!dateVal) return true;
              if (recordDate >= dateVal) return false;
              break;
            case "gt":
              if (!dateVal) return true;
              if (recordDate <= dateVal) return false;
              break;
            case "between":
              if (!start || !end) return true;
              if (recordDate < start || recordDate > end) return false;
              break;
            default:
              break;
          }
        }

        return true;
      });
    },
    [
      appliedDqaApproval,
      appliedPmApproval,
      appliedDateField,
      appliedCondition,
      appliedDateValue,
      appliedStartDate,
      appliedEndDate,
      userRole,
      isSuperAdmin,
      parseDDMMYYYY,
      parseYYYYMMDD,
    ],
  );

  useEffect(() => {
    const loadData = async () => {
      if (!village_id) {
        setLoadingState(false);
        return;
      }

      const apiData = await fetchFeaturesFromAPI();

      if (apiData) {
        const filteredFeatures = getFilteredFeatures(apiData.features);
        setFeatures(filteredFeatures);
        setVillageInfo(apiData.villageInfo);

        const sortedData = (filteredFeatures || []).sort(
          (a, b) =>
            new Date(b.pm_approved_at || b.dqa_approved_at || 0) -
            new Date(a.pm_approved_at || a.dqa_approved_at || 0),
        );

        setRolesData(sortedData);
      } else {
        setVillageInfo(null);
        setRolesData([]);
      }

      setLoadingState(false);
    };

    loadData();
  }, [
    village_id,
    appliedDqaApproval,
    appliedPmApproval,
    appliedDateField,
    appliedCondition,
    appliedDateValue,
    appliedStartDate,
    appliedEndDate,
    fetchFeaturesFromAPI,
    getFilteredFeatures,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search === debouncedSearch) return;
      setDebouncedSearch(search);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Show approve modal
  const showApproveModal = (id) => {
    setSelectedApproveId(id);
    setApproveModalVisible(true);
  };

  // Show reject modal
  const showRejectModal = (id) => {
    setSelectedRejectId(id);
    setRejectModalVisible(true);
  };

  // Handle approval from modal
  const handleApprove = async () => {
    setApproveModalVisible(false);
    try {

      // Determine approval type based on user role and current state
      let approvalType = "";
      const feature = features.find(
        (f) => f.boqs_proposed_implementation_id === selectedApproveId,
      );

      if (!feature) {
        toast.error("Feature not found");
        return;
      }

      // Check if user is DQA or PM
      if (userRole === "dqa" || (isSuperAdmin && userRole === "dqa")) {
        approvalType = "dqa-approve";
      } else if (
        userRole === "project manager" ||
        (isSuperAdmin && userRole === "project manager")
      ) {
        approvalType = "pm-approve";
      } else if (isSuperAdmin) {
        // For Super Admin, check current state to determine which type to use
        // If DQA is not approved (0, 2, null), use dqa-approve
        // If DQA is approved (1), use pm-approve
        approvalType =
          feature.is_dqa_approved === 1 ? "pm-approve" : "dqa-approve";
      }

      const endpoint = `implementation-phase-details/approve/${selectedApproveId}`;
      const response = await fetchData(
        endpoint,
        "PUT",
        { type: approvalType },
        {},
        {},
        navigate,
        null,
        dispatch,
      );

      if (response?.success) {
        // Refresh data instead of reloading page
        const apiData = await fetchFeaturesFromAPI();
        if (apiData) {
          const filteredFeatures = getFilteredFeatures(apiData.features);

          setFeatures(filteredFeatures);

          const sortedData = (filteredFeatures || []).sort(
            (a, b) =>
              new Date(b.pm_approved_at || b.dqa_approved_at || 0) -
              new Date(a.pm_approved_at || a.dqa_approved_at || 0),
          );
          setRolesData(sortedData);
        }
        toast.success(`Feature approved successfully by ${userRole}`);
      } else {
        toast.error(response?.message || "Failed to approve feature");
      }
    } catch (error) {
      console.error("Error approving feature:", error);
      toast.error("Failed to approve feature");
    }
  };

  // Handle reject (shows remarks modal)
  const handleReject = async () => {
    setRejectModalVisible(false);
    setRemarksModalVisible(true);
  };

  const handleSubmitRemarks = async () => {
    const validationError = validateRemarks(remarks);
    if (validationError) {
      setRemarksError(validationError);
      return;
    }

    try {
      // Determine rejection type based on user role and current state
      let rejectionType = "";
      const feature = features.find(
        (f) => f.boqs_proposed_implementation_id === selectedRejectId,
      );

      if (!feature) {
        toast.error("Feature not found");
        return;
      }

      // Check current user role (normalize to lowercase for comparison)
      const currentUserRole = (userRole || "").toLowerCase();

      // Determine which approval level we're at
      const isDqaApproved = feature.is_dqa_approved === 1;
      const isDqaRejected = feature.is_dqa_approved === 2;
      const isDqaPending =
        feature.is_dqa_approved === 0 || feature.is_dqa_approved === null;

      if (isSuperAdmin) {
        // Super Admin logic - check the current state
        if (isDqaApproved) {
          // DQA has approved, so we're at PM level
          rejectionType = "pm-reject";
        } else {
          // DQA hasn't approved yet, so we're at DQA level
          rejectionType = "dqa-reject";
        }
      } else {
        // Regular user logic
        if (currentUserRole === "dqa") {
          rejectionType = "dqa-reject";
        } else if (currentUserRole === "project manager") {
          // PM can only reject if DQA has approved
          if (!isDqaApproved) {
            toast.error("Cannot reject: DQA must approve before PM can reject");
            return;
          }
          rejectionType = "pm-reject";
        } else {
          toast.error("You don't have permission to reject features");
          return;
        }
      }


      const endpoint = `implementation-phase-details/reject/${selectedRejectId}`;

      const response = await fetchData(
        endpoint,
        "PUT",
        {
          type: rejectionType, // Make sure this is using the variable
          remarks: remarks.trim(),
        },
        {},
        {},
        navigate,
        null,
        dispatch,
      );

      if (response?.success) {
        // Refresh data instead of reloading page
        const apiData = await fetchFeaturesFromAPI();
        if (apiData) {
          const filteredFeatures = getFilteredFeatures(apiData.features);
          setFeatures(filteredFeatures);
          const sortedData = (filteredFeatures || []).sort(
            (a, b) =>
              new Date(b.pm_approved_at || b.dqa_approved_at || 0) -
              new Date(a.pm_approved_at || a.dqa_approved_at || 0),
          );
          setRolesData(sortedData);
        }
        toast.success(`Feature rejected successfully by ${userRole}`);
        setRemarksModalVisible(false);
        setRemarks("");
        setRemarksError("");
      } else {
        toast.error(response?.message || "Failed to reject feature");
      }
    } catch (error) {
      console.error("Error rejecting feature:", error);
      toast.error("Failed to reject feature");
    }
  };
  // Cancel remarks
  const handleCancelRemarks = () => {
    setRemarksModalVisible(false);
    setRemarks("");
    setRemarksError("");
  };

  const filteredFeatures = useMemo(() => {
    return rolesData.filter((feature) => {
      const searchLower = debouncedSearch.toLowerCase();
      return (
        feature.feature?.toLowerCase().includes(searchLower) ||
        feature.category_name?.toLowerCase().includes(searchLower)
      );
    });
  }, [rolesData, debouncedSearch]);

  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;

  const paginatedFeatures = useMemo(() => {
    return filteredFeatures.slice(startIndex, startIndex + pagination.pageSize);
  }, [filteredFeatures, startIndex, pagination.pageSize]);

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
      totalPages: Math.ceil(filteredFeatures.length / size),
    }));
  };

  const columns = useMemo(
    () => [
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">Sr.No</span>
        ),
        cell: (row, index) => <span>{startIndex + index + 1}</span>,
        width: "80px",
      },
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">
            Village
          </span>
        ),
        selector: (row) => row.village_name,
        sortable: true,
        grow: 2,
        width: "200px",
      },
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">
            Feature
          </span>
        ),
        selector: (row) => row.feature,
        sortable: true,
        grow: 2,
      },
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">
            Feature Category
          </span>
        ),
        selector: (row) => row.category_name,
        sortable: true,
        grow: 2,
        width: "200px",
      },
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">
            Feature Code
          </span>
        ),
        selector: (row) => row.feature_code,
        sortable: true,
        center: true,
        width: "150px",
        cell: (row) => (
          <span className="font-semibold text-gray-700">
            {row.feature_code || "N/A"}
          </span>
        ),
      },
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">
            Overall Status
          </span>
        ),
        cell: (row) => {
          const statusColors = {
            Planned: "bg-blue-100 text-blue-800",
            "In Progress": "bg-yellow-100 text-yellow-800",
            Completed: "bg-[#137e81] text-white",
            Postponed: "bg-orange-100 text-orange-800",
            "Cancelled / Excluded": "bg-red-100 text-red-800",
          };

          const colorClass =
            statusColors[row.overall_status] || "bg-gray-100 text-gray-800";

          return (
            <span
              className={`py-1 px-2 text-[0.75rem] rounded-full font-semibold ${colorClass}`}
            >
              {row.overall_status}
            </span>
          );
        },
        sortable: true,
        center: true,
        width: "150px",
      },
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">
            Date of Visit
          </span>
        ),
        selector: (row) => row.record_date,
        sortable: true,
        center: true,
        width: "130px",
      },
      // DQA Approval Column - Only for DQA and Super Admin
      // ...(userRole === "dqa" || isSuperAdmin
      //   ? [
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">
            DQA Approval
          </span>
        ),
        cell: (row) => {
          if (row.is_dqa_approved === 1) {
            return (
              <div className="text-center">
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-blue-100 text-blue-800">
                  Approved
                </span>
                {row.verified_from_dqa_datetime && (
                  <div className="text-[0.65rem] text-gray-600 mt-2">
                    {formatApprovalDateTime(row.verified_from_dqa_datetime)}
                  </div>
                )}
              </div>
            );
          } else if (row.is_dqa_approved === 2) {
            return (
              <div className="text-center">
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-red-100 text-red-800">
                  Rejected
                </span>
                {row.verified_from_dqa_datetime && (
                  <div className="text-[0.65rem] text-gray-600 mt-1">
                    {formatApprovalDateTime(row.verified_from_dqa_datetime)}
                  </div>
                )}
              </div>
            );
          } else {
            return (
              <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-yellow-100 text-yellow-800">
                Pending
              </span>
            );
          }
        },
        center: true,
        width: "120px",
      },
      //   ]
      // : []),

      // PM Approval Column - Only for Project Manager and Super Admin
      // ...(userRole === "project manager" || isSuperAdmin
      //   ? [
      {
        name: (
          // <span className="text-[0.9rem] text-[#212529] uppercase">
          <span className="text-[0.9rem] text-[#212529] uppercase">
            PM Approval
          </span>
        ),
        cell: (row) => {
          const isDQAApproved = row.is_dqa_approved === 1;
          const isDQARejected = row.is_dqa_approved === 2;
          const isDQAPending =
            row.is_dqa_approved === 0 || row.is_dqa_approved === null;

          if (isDQAPending) {
            return (
              <div className="text-center flex flex-col items-center">
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-yellow-100 text-yellow-800">
                  Waiting for DQA
                </span>
              </div>
            );
          }

          if (isDQARejected) {
            return (
              <div className="text-center flex flex-col items-center">
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-red-100 text-red-800">
                  DQA Rejected
                </span>
              </div>
            );
          }

          if (row.is_pm_approved === 1) {
            return (
              <div className="text-center">
                {/* <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-green-100 text-green-800"> */}
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-blue-100 text-blue-800">
                  Approved
                </span>
                {row.verified_from_pm_datetime && (
                  <div className="text-[0.65rem] text-gray-600 mt-1" >
                    {formatApprovalDateTime(row.verified_from_pm_datetime)}
                  </div>
                )}
              </div>
            );
          }

          if (row.is_pm_approved === 2) {
            return (
              <div className="text-center">
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-red-100 text-red-800">
                  Rejected
                </span>
                {row.verified_from_pm_rejected_at && (
                  <div className="text-[0.65rem] text-gray-600 mt-1">
                    {formatApprovalDateTime(row.verified_from_pm_rejected_at)}
                  </div>
                )}
              </div>
            );
          }

          return (
            <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-yellow-100 text-yellow-800">
              Pending
            </span>
          );
        },
        center: true,
        width: "120px",
      },
      //   ]
      // : []),

      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">Action</span>
        ),
        cell: (row) => {
          const hasRemarks = hasAnyRemarks(row);
          const { showDqaButtons, showPmButtons } = getButtonVisibility(row);
          const isDqa = userRole === "dqa";
          const isPm = userRole === "project manager";
          const isSuperAdminUser = isSuperAdmin === true;

               const isDQAApproved = row.is_dqa_approved === 1;
               const isDQARejected = row.is_dqa_approved === 2;

          /* =======================
           DQA OR SUPER ADMIN
        ======================= */
          if (isDqa || isSuperAdminUser) {
            return (
              <div className="flex gap-2 items-center">
                {/* Remarks */}
                {hasRemarks && (
                  <button
                    onClick={() => showRemarksDialog(row)}
                    title="View Remarks"
                    className="hover:scale-110 transition-transform"
                  >
                    <i className="fa-solid fa-comment fa-lg text-blue-600"></i>
                  </button>
                )}

                {/* Edit */}
                <Link
                  to={`/implementation-phase/manage/${village_id}/feature/${row.id}`}
                  title="View"
                  // faIcon={
                  //   isDQAApproved || isDQARejected
                  //     ? "fa-solid fa-eye fa-lg"
                  //     : "fa-solid fa-pen-to-square fa-lg"
                  // }
                >
                  {/* <i className="fa-solid fa-pen-to-square fa-lg text-black"></i> */}
                  <i className="fa-solid fa-eye fa-lg"></i>
                </Link>

                {/* DQA buttons (ONLY DQA + SuperAdmin) */}
                {showDqaButtons && row.overall_status === "Completed" && (isDqa || isSuperAdminUser) && (
                  <>
                    <button
                      onClick={() =>
                        row.boqs_proposed_implementation_id &&
                        showApproveModal(row.boqs_proposed_implementation_id)
                      }
                      title="DQA Approve"
                      className="hover:scale-110 transition-transform"
                    >
                      <i className="fa-solid fa-check-circle fa-lg text-green-600"></i>
                    </button>

                    <button
                      onClick={() =>
                        row.boqs_proposed_implementation_id &&
                        showRejectModal(row.boqs_proposed_implementation_id)
                      }
                      title="DQA Reject"
                      className="hover:scale-110 transition-transform"
                    >
                      <i className="fa-solid fa-times-circle fa-lg text-red-600"></i>
                    </button>
                  </>
                )}

                {/* PM buttons (ONLY Super Admin) */}
                {isSuperAdminUser && showPmButtons && (
                  <>
                    <button
                      onClick={() =>
                        showApproveModal(row.boqs_proposed_implementation_id)
                      }
                      title="PM Approve"
                      className="hover:scale-110 transition-transform"
                    >
                      <i className="fa-solid fa-check-circle fa-lg text-blue-600"></i>
                    </button>

                    <button
                      onClick={() =>
                        showRejectModal(row.boqs_proposed_implementation_id)
                      }
                      title="PM Reject"
                      className="hover:scale-110 transition-transform"
                    >
                      <i className="fa-solid fa-times-circle fa-lg text-orange-600"></i>
                    </button>
                  </>
                )}
              </div>
            );
          }

          /* =======================
            PROJECT MANAGER
        ======================= */
          if (isPm) {
            return (
              <div className="flex gap-2 items-center">
                {/* Remarks */}
                {hasRemarks && (
                  <button
                    onClick={() => showRemarksDialog(row)}
                    title="View Remarks"
                    className="hover:scale-110 transition-transform"
                  >
                    <i className="fa-solid fa-comment fa-lg text-blue-600"></i>
                  </button>
                )}

                {/* Edit */}
                <Link
                  to={`/implementation-phase/manage/${village_id}/feature/${row.id}`}
                  title="View"
                >
                  {/* <i className="fa-solid fa-pen-to-square fa-lg text-black"></i> */}
                  <i className="fa-solid fa-eye fa-lg"></i>
                </Link>

                {/* PM buttons ONLY - show when DQA approved and PM pending */}
                {showPmButtons && row.overall_status === "Completed" && (
                  <>
                    <button
                      onClick={() =>
                        showApproveModal(row.boqs_proposed_implementation_id)
                      }
                      title="PM Approve"
                      className="hover:scale-110 transition-transform"
                    >
                      <i className="fa-solid fa-check-circle fa-lg text-green-600"></i>
                    </button>

                    <button
                      onClick={() =>
                        showRejectModal(row.boqs_proposed_implementation_id)
                      }
                      title="PM Reject"
                      className="hover:scale-110 transition-transform"
                    >
                      <i className="fa-solid fa-times-circle fa-lg text-red-600"></i>
                    </button>
                  </>
                )}
              </div>
            );
          }

          /* =======================
            OTHER USERS (VIEW ONLY)
        ======================= */
          return (
            <div className="flex gap-2 items-center">
              {hasRemarks && (
                <button
                  onClick={() => showRemarksDialog(row)}
                  title="View Remarks"
                  className="hover:scale-110 transition-transform"
                >
                  <i className="fa-solid fa-comment fa-lg text-blue-600"></i>
                </button>
              )}

              <Link
                to={`/implementation-phase/manage/${village_id}/feature/${row.id}`}
                title="View"
              >
                <i className="fa-solid fa-eye fa-lg text-black"></i>
              </Link>
            </div>
          );
        },
        center: true,
        width: "150px",
      },
    ],
    [startIndex, village_id, isSuperAdmin, userRole],
  );
  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "0.875rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
        fontSize: "0.8rem",
      },
    },
  };

  useEffect(() => {
    const totalPages = Math.ceil(filteredFeatures.length / pagination.pageSize);
    setPagination((prev) => ({
      ...prev,
      totalPages,
      totalItems: filteredFeatures.length,
    }));
  }, [filteredFeatures.length, pagination.pageSize]);

  if (loadingState) return <Loader />;

  if (!villageInfo) {
    return (
      <div className="w-full ">
        <div className="my-4 w-full flex flex-col justify-between space-y-3">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
            <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
              Implementation Phase Information
            </h1>
          </div>
        </div>
        <div className="rounded-lg my-[30px] shadow w-full bg-white p-4">
          <div className="text-center py-8">
            <h2 className="text-xl text-gray-600">
              Proposed Features not found
            </h2>
            <p className="text-gray-500 mt-2">
              No data found for ID: {village_id}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f6b6e] transition-colors duration-200"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full ">
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Implementation Phase Information
          </h1>

          {hasDataToExport && (
            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                isExporting
                  ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                  : "bg-[#137e81] text-white hover:bg-[#0f6c6f] hover:scale-105"
              }`}
              title="Export filtered data to Excel"
            >
              <Download size={18} />
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
          )}
        </div>
      </div>

      <Accordion>
        <AccordionSummary
          expandIcon={
            isFiltersVisible ? (
              <Minus color="#ffff" size={14} />
            ) : (
              <Plus size={14} color="#ffff" />
            )
          }
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            backgroundColor: "#137e81",
            color: "#fff !important",
            "& .MuiAccordionSummary-content": { color: "#fff !important" },
          }}
          onClick={toggleFilters}
        >
          Filters
        </AccordionSummary>
        <AccordionDetails>
          <div className="space-y-4">
            {/* Approval Filters */}
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-5 w-full">
              <div className="flex-1">
                <ThinDropdownWithSearch required={false}
                  key={`dqa-${filterReset}`}
                  label="DQA Approval"
                  id="dqa-approval-dropdown"
                  options={approvalOptions}
                  value={selectedDqaApproval}
                  onChange={setSelectedDqaApproval}
                />
              </div>
              <div className="flex-1">
                <ThinDropdownWithSearch required={false}
                  key={`pm-${filterReset}`}
                  label="PM Approval"
                  id="pm-approval-dropdown"
                  options={approvalOptions}
                  value={selectedPmApproval}
                  onChange={setSelectedPmApproval}
                />
              </div>

              <div className="flex-1">
                <ThinDropdownWithSearch required={false}
                  label="Date Field"
                  id="date-field-dropdown"
                  options={dateFieldsOptions}
                  value={dateField}
                  onChange={(value) => handleFilterChange("dateField", value)}
                />
              </div>

              <div className="flex-1">
                <ThinDropdownWithSearch required={false}
                  label="Condition"
                  id="condition-dropdown"
                  options={conditions}
                  value={condition}
                  onChange={(value) => handleFilterChange("condition", value)}
                  disabled={!dateField}
                />
              </div>
              {/* Date Filters */}

              {condition === "between" ? (
                <>
                  <div className="flex-1">
                    <ThinTextField isRequired={false}
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <ThinTextField isRequired={false}
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-1">
                    <ThinTextField isRequired={false}
                      label="Date Value"
                      type="date"
                      value={dateValue}
                      onChange={(e) => setDateValue(e.target.value)}
                      disabled={!condition || condition === "between"}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 mt-4 justify-end">
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#a68108] transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-[#137e81] text-white rounded-lg transition-colors"
            >
              Apply
            </button>
          </div>
        </AccordionDetails>
      </Accordion>

      <div className="rounded-lg my-[30px] shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Manage Implementation Information
          </h1>

          {(hasFiltersApplied || search) && (
            <div className="text-sm text-gray-600">
              {filteredFeatures.length} records found
              {hasDataToExport && (
                <span className="ml-2 text-green-600 font-medium">
                  • Export available
                </span>
              )}
            </div>
          )}
        </div>

        <div className="p-[1.25rem] w-full">
          {features.length == 0 ? (
            <div className="text-center py-8">
              <h2 className="text-xl text-gray-600">
                No implementation records found
              </h2>
              <p className="text-gray-500 mt-2">
                No implementation phase records have been created for this
                village yet.
              </p>
            </div>
          ) : (
            <>
              <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
                <PaginationDropdown
                  value={pagination.pageSize}
                  onChange={handlePageSizeChange}
                  options={[
                    { value: 10, label: "10" },
                    { value: 20, label: "20" },
                    { value: 30, label: "30" },
                  ]}
                />

                <input
                  onChange={(e) => setSearch(e.target.value)}
                  value={search}
                  placeholder="Search features..."
                  type="search"
                  className="font-[Light] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none"
                />
              </div>

              <DataTable
                columns={columns}
                persistTableHead
                data={paginatedFeatures}
                customStyles={customStyles}
                highlightOnHover
                progressPending={false}
              />

              <GeneralFooter
                pagination={pagination}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
                dataLength={filteredFeatures.length}
              />
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <CustomConfirmModal
        isVisible={isApproveModalVisible}
        onConfirm={handleApprove}
        onCancel={() => setApproveModalVisible(false)}
        message="Are you sure you want to approve this feature?"
      />

      <CustomConfirmModal
        isVisible={isRejectModalVisible}
        onConfirm={handleReject}
        onCancel={() => setRejectModalVisible(false)}
        message="Are you sure you want to reject this feature?"
      />

      {/* Rejection Remarks Dialog - SAME AS HOUSEHOLD PAGE */}
      <Dialog
        open={isRemarksModalVisible}
        onClose={handleCancelRemarks}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#137e81",
            color: "white",
            padding: "16px 24px",
            fontSize: "1.25rem",
            fontWeight: "bold",
          }}
        >
          Rejection Remarks
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Box sx={{ marginBottom: "16px" }}>
            <Typography
              variant="body1"
              sx={{
                marginBottom: "8px",
                color: "#333",
                fontWeight: "500",
              }}
            >
              Please provide the reason for rejection:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                fontStyle: "italic",
              }}
            >
              Remarks must be between 10 and 255 characters.
            </Typography>
          </Box>
          <TextField
            autoFocus
            label="Remarks"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={remarks}
            onChange={(e) => handleRemarksChange(e.target.value)}
            placeholder="Enter detailed reason for rejection (minimum 10 characters, maximum 255 characters)..."
            variant="outlined"
            error={!!remarksError}
            helperText={
              remarksError ||
              `${remarks.trim().length}/255 characters` +
                (remarks.trim().length > 0
                  ? ` (${
                      remarks.trim().length < 10
                        ? `${10 - remarks.trim().length} more required`
                        : "valid length"
                    })`
                  : "")
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#137e81",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#137e81",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#137e81",
              },
              "& .MuiFormHelperText-root": {
                color: remarksError ? "#d32f2f" : "#666",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", gap: "8px" }}>
          <Button
            onClick={handleCancelRemarks}
            color="secondary"
            variant="outlined"
            sx={{
              borderColor: "#6c757d",
              color: "#6c757d",
              "&:hover": {
                borderColor: "#545b62",
                backgroundColor: "rgba(108, 117, 125, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRemarks}
            color="primary"
            variant="contained"
            disabled={
              !remarks.trim() ||
              remarks.trim().length < 10 ||
              remarks.trim().length > 255
            }
            sx={{
              backgroundColor: "#137e81",
              "&:hover": {
                backgroundColor: "#0f6c6f",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
                color: "#666666",
              },
            }}
          >
            Submit Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Remarks History Dialog - SAME AS HOUSEHOLD PAGE */}
      <Dialog
        open={isViewRemarksModalVisible}
        onClose={closeRemarksDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            maxWidth: "600px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#137e81",
            color: "white",
            padding: "16px 24px",
            fontSize: "1.25rem",
            fontWeight: "bold",
          }}
        >
          Remarks History
          {selectedFeatureForRemarks && (
            <Typography
              variant="subtitle1"
              sx={{ color: "white", fontSize: "0.9rem", mt: 1 }}
            >
              Feature: {selectedFeatureForRemarks.feature} -{" "}
              {selectedFeatureForRemarks.feature_code}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ padding: "24px", maxHeight: "60vh" }}>
          {selectedFeatureForRemarks && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: "#137e81" }}>
                {/* Remarks History */}
              </Typography>

              {getRemarksHistory(selectedFeatureForRemarks).length > 0 ? (
                <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
                  {getRemarksHistory(selectedFeatureForRemarks).map(
                    (history, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          backgroundColor: index === 0 ? "#f0f8ff" : "white",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: "bold", color: "#333" }}
                          >
                            {/* Update #{getRemarksHistory(selectedFeatureForRemarks).length - index} */}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            {formatApprovalDateTime(history.date)}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Remarks :</strong>{" "}
                            <span
                              style={{ color: "#2e7d32", fontWeight: "bold" }}
                            >
                              {history.to ? history.to : "N/A"}
                            </span>
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{ color: "#666", mt: 1, display: "block" }}
                        >
                          User Name: {history.userName}
                        </Typography>
                      </Box>
                    ),
                  )}
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", color: "#666", py: 3 }}
                >
                  No remarks history available
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={closeRemarksDialog}
            color="primary"
            variant="contained"
            sx={{
              backgroundColor: "#137e81",
              "&:hover": {
                backgroundColor: "#0f6c6f",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ImplementationPhaseFeatuesList;
